import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { recommendationsAPI, statsAPI } from '../services/api';
import { onNewSuggestions, onTradesUpdated, onTradesStarted, offEventHandler } from '../services/socket';
import { usdt } from '../utils/currency';
import BotScanner from '../components/BotScanner';
import api from '../services/api';
import './Trading.css';

const Trading = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('pending');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [pendingSuggestions, setPendingSuggestions] = useState([]);
  const [activeTrades, setActiveTrades] = useState([]);
  const [completedTrades, setCompletedTrades] = useState([]);
  const [expiredSuggestions, setExpiredSuggestions] = useState([]);
  const [botStats, setBotStats] = useState(null);
  const [botUniverse, setBotUniverse] = useState({ total: 200, coins: [] }); // coins du scanner

  // Charger l'univers des coins pour l'animation du scanner du bot
  useEffect(() => {
    api.get('/crypto/bot-universe')
      .then(r => { if (r.data?.coins?.length) setBotUniverse(r.data); })
      .catch(() => {}); // le composant a une liste de secours
  }, []);

  // Mettre à jour l'heure chaque seconde
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch data from API
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [pendingRes, activeRes, completedRes, expiredRes, botRes] = await Promise.all([
        recommendationsAPI.getPending(),
        recommendationsAPI.getActive(),
        recommendationsAPI.getCompleted(),
        recommendationsAPI.getExpired(),
        statsAPI.getBot()
      ]);

      setPendingSuggestions(pendingRes.data.suggestions || []);
      setActiveTrades(activeRes.data.activeTrades || []);
      setCompletedTrades(completedRes.data.completedTrades || []);
      setExpiredSuggestions(expiredRes.data.missedSuggestions || []);
      setBotStats(botRes.data);
    } catch (error) {
      console.error('Error fetching trading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Filet de sécurité: rafraîchissement toutes les 30 secondes
    const interval = setInterval(fetchData, 30000);

    // Temps réel: rafraîchir instantanément dès qu'un événement serveur arrive
    // (nouvelles suggestions, trade démarré, trade clôturé) → on ne rate rien.
    onNewSuggestions(fetchData);
    onTradesUpdated(fetchData);
    onTradesStarted(fetchData);

    return () => {
      clearInterval(interval);
      offEventHandler('new_suggestions', fetchData);
      offEventHandler('trades_updated', fetchData);
      offEventHandler('trades_started', fetchData);
    };
  }, [fetchData]);

  // Handle accept suggestion
  const handleAccept = async (id) => {
    try {
      await recommendationsAPI.accept(id);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || t('trading.acceptError'));
    }
  };

  // Handle reject suggestion
  const handleReject = async (id) => {
    try {
      await recommendationsAPI.reject(id);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || t('trading.rejectError'));
    }
  };

  // Vérifie si le trade est loupé
  const isMissed = (startTime) => {
    if (!startTime) return true;
    const startDate = new Date(startTime);
    if (isNaN(startDate.getTime())) return true;
    return startDate <= currentTime;
  };

  // Formate le temps restant
  const formatTimeRemaining = (startTime) => {
    if (!startTime) return t('trading.expired');

    const startDate = new Date(startTime);
    if (isNaN(startDate.getTime())) return t('trading.expired');

    const diff = startDate - currentTime;
    if (diff <= 0) return t('trading.expired');

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (isNaN(minutes) || isNaN(seconds)) return t('trading.expired');

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  // Formate l'heure
  const formatTime = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calcule le progrès et temps restant d'un trade actif
  const getTradeProgress = (startedAt, endTime) => {
    const start = new Date(startedAt).getTime();
    const end = new Date(endTime).getTime();
    const now = currentTime.getTime();

    const total = end - start;
    const elapsed = now - start;
    const remaining = end - now;

    const progress = Math.min(100, Math.max(0, (elapsed / total) * 100));

    if (remaining <= 0) return { progress: 100, remaining: t('trading.closed'), isFinished: true };

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    let remainingText = '';
    if (hours > 0) remainingText = `${hours}h ${minutes}m`;
    else if (minutes > 0) remainingText = `${minutes}m ${seconds}s`;
    else remainingText = `${seconds}s`;

    return { progress, remaining: remainingText, isFinished: false };
  };

  const formatCurrency = (value) => usdt(value);

  // Combine pending and expired for display
  const allSuggestions = [...pendingSuggestions, ...expiredSuggestions];

  return (
    <Layout>
      <div className="trading-page">
        {/* Bot Status Card */}
        <div className="trading-bot-section">
          <div className="trading-bot-card">
            {/* Background effects */}
            <div className="bot-bg-effects">
              <div className="scan-line"></div>
              <div className="pulse-ring pulse-1"></div>
              <div className="pulse-ring pulse-2"></div>
              <div className="pulse-ring pulse-3"></div>
              <div className="floating-particle p1"></div>
              <div className="floating-particle p2"></div>
              <div className="floating-particle p3"></div>
              <div className="floating-particle p4"></div>
              <div className="grid-overlay"></div>
            </div>

            <div className="bot-content">
              <div className="bot-status">
                {/* Animated SVG Robot */}
                <div className="bot-avatar">
                  <svg viewBox="0 0 100 100" className="robot-svg">
                    <g className="antenna">
                      <line x1="50" y1="15" x2="50" y2="5" stroke="#1e88e5" strokeWidth="3" strokeLinecap="round"/>
                      <circle cx="50" cy="3" r="4" fill="#0ecb81" className="antenna-light"/>
                    </g>
                    <rect x="25" y="15" width="50" height="45" rx="10" fill="#1e3a5f" stroke="#1e88e5" strokeWidth="2"/>
                    <rect x="30" y="22" width="40" height="30" rx="5" fill="#0b1929"/>
                    <g className="eyes">
                      <circle cx="40" cy="37" r="6" fill="#0ecb81" className="eye left-eye"/>
                      <circle cx="60" cy="37" r="6" fill="#0ecb81" className="eye right-eye"/>
                      <circle cx="40" cy="37" r="2" fill="#fff" className="pupil"/>
                      <circle cx="60" cy="37" r="2" fill="#fff" className="pupil"/>
                    </g>
                    <rect x="38" y="45" width="24" height="4" rx="2" fill="#1e88e5" className="mouth"/>
                    <rect x="30" y="62" width="40" height="25" rx="5" fill="#1e3a5f" stroke="#1e88e5" strokeWidth="2"/>
                    <circle cx="50" cy="74" r="5" fill="#f0b90b" className="chest-light"/>
                    <rect x="15" y="65" width="12" height="6" rx="3" fill="#1e3a5f" stroke="#1e88e5" strokeWidth="1.5" className="arm left-arm"/>
                    <rect x="73" y="65" width="12" height="6" rx="3" fill="#1e3a5f" stroke="#1e88e5" strokeWidth="1.5" className="arm right-arm"/>
                  </svg>
                  <div className="bot-glow"></div>
                </div>

                <div className="bot-info">
                  <span className="bot-title">{t('dashboard.tradingBot')}</span>
                  <span className="bot-state active">
                    <span className="status-dot"></span>
                    {t('dashboard.botAnalyzing')}
                  </span>
                </div>
              </div>

              <BotScanner
                coins={botUniverse.coins}
                labels={{
                  validated: t('dashboard.botSignalValidated') || 'Signal validé',
                  rejected: t('dashboard.botSignalRejected') || 'Écarté',
                  scanning: t('dashboard.botScanning') || 'Analyse'
                }}
              />

              <div className="bot-stats">
                <div className="bot-stat">
                  <span className="stat-value">
                    {botStats ? (
                      (botStats.maxTradesPerDay - botStats.baseTradesPerDay) > 0
                        ? <>{botStats.baseTradesPerDay}<span className="stat-extra">+{botStats.maxTradesPerDay - botStats.baseTradesPerDay}</span></>
                        : botStats.maxTradesPerDay
                    ) : '—'}
                  </span>
                  <span className="stat-label">{t('trading.suggestionsPerDay')}</span>
                </div>
                <div className="bot-stat">
                  <span className="stat-value">{botStats ? `${botStats.successRate}%` : '—'}</span>
                  <span className="stat-label">{t('dashboard.successRate')}</span>
                </div>
                <div className="bot-stat">
                  <span className="stat-value">{botStats ? `${botStats.averageGain}%` : '—'}</span>
                  <span className="stat-label">{t('trading.averageGain')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Compteur de signaux journaliers */}
        {botStats && (
          <div className="signal-counter">
            <div className="signal-counter-left">
              <span className="signal-counter-label">{t('trading.signalsToday')}</span>
              <div className="signal-dots">
                {Array.from({ length: botStats.maxTradesPerDay || 0 }).map((_, i) => (
                  <span
                    key={i}
                    className={`signal-dot ${i < (botStats.receivedToday || 0) ? 'received' : 'pending-dot'}`}
                  />
                ))}
              </div>
            </div>
            <span className="signal-counter-value">
              {botStats.receivedToday ?? 0}
              <span className="signal-counter-sep">/</span>
              {botStats.maxTradesPerDay ?? 0}
            </span>
          </div>
        )}

        {/* Tabs */}
        <div className="trading-tabs">
          <button
            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <span className="tab-icon">🔔</span>
            {t('trading.suggestions')}
            {pendingSuggestions.length > 0 && (
              <span className="tab-badge">{pendingSuggestions.length}</span>
            )}
          </button>
          <button
            className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            <span className="tab-icon">⏳</span>
            {t('trading.inProgress')}
            {activeTrades.length > 0 && (
              <span className="tab-badge">{activeTrades.length}</span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="trading-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>{t('common.loading')}</p>
            </div>
          ) : activeTab === 'pending' ? (
            (allSuggestions.length > 0 || completedTrades.length > 0) ? (
              <div className="suggestions-list">
                {/* Trades validés (succès) - Design compact */}
                {completedTrades.slice(0, 3).map((trade) => {
                  const isWin = trade.actualGain >= 0;
                  return (
                  <div key={trade.id} className={`compact-card ${isWin ? 'success' : 'loss'}`}>
                    {/* Header compact avec crypto et badge */}
                    <div className="compact-header">
                      <div className="compact-crypto">
                        <div
                          className="compact-avatar"
                          style={{ background: `linear-gradient(135deg, ${trade.gradient[0]}, ${trade.gradient[1]})` }}
                        >
                          {trade.icon}
                        </div>
                        <div className="compact-crypto-info">
                          <span className="compact-symbol">{trade.symbol}</span>
                          <span className="compact-name">{trade.name}</span>
                        </div>
                      </div>
                      <div className="compact-badges">
                        <span className={`compact-type ${trade.type === 'ACHAT' ? 'buy' : 'sell'}`}>
                          {trade.type === 'ACHAT' ? t('trading.buy') : t('trading.sell')}
                        </span>
                        <span className={`compact-status ${isWin ? 'success' : 'loss'}`}>{t('trading.validated')} ✓</span>
                      </div>
                    </div>

                    {/* Infos en ligne compacte */}
                    <div className="compact-details">
                      <div className="compact-detail">
                        <span className="detail-label">{t('trading.invested')}</span>
                        <span className="detail-value">{formatCurrency(trade.amount)}</span>
                      </div>
                      <div className="compact-divider"></div>
                      <div className="compact-detail">
                        <span className="detail-label">{t('trading.duration')}</span>
                        <span className="detail-value">{trade.duration || '—'}</span>
                      </div>
                      <div className="compact-divider"></div>
                      <div className={`compact-detail gain ${isWin ? '' : 'loss'}`}>
                        <span className="detail-label">{t('trading.gain')}</span>
                        <span className="detail-value">{isWin ? '+' : ''}{formatCurrency(trade.actualGain)}</span>
                      </div>
                    </div>

                    {/* Footer résultat */}
                    <div className={`compact-success ${isWin ? '' : 'loss'}`}>
                      <span className="success-check">{isWin ? '✓' : '−'}</span>
                      <span className="success-msg">{isWin ? t('trading.gainsPaid') : t('trading.lossPaid')}</span>
                      <span className="success-time">{formatTime(trade.completedAt)}</span>
                    </div>
                  </div>
                  );
                })}

                {/* Suggestions en attente et loupées - Design compact */}
                {allSuggestions.map((suggestion) => {
                  const missed = suggestion.status === 'expired' || isMissed(suggestion.startTime);
                  const timeRemaining = formatTimeRemaining(suggestion.startTime);

                  return (
                    <div key={suggestion.id} className={`compact-card ${missed ? 'missed' : 'pending'}`}>
                      {/* Header compact avec crypto et badge */}
                      <div className="compact-header">
                        <div className="compact-crypto">
                          <div
                            className="compact-avatar"
                            style={{ background: `linear-gradient(135deg, ${suggestion.gradient[0]}, ${suggestion.gradient[1]})` }}
                          >
                            {suggestion.icon}
                          </div>
                          <div className="compact-crypto-info">
                            <span className="compact-symbol">{suggestion.symbol}</span>
                            <span className="compact-name">{suggestion.name}</span>
                          </div>
                        </div>
                        <div className="compact-badges">
                          <span className={`compact-type ${suggestion.type === 'ACHAT' ? 'buy' : 'sell'}`}>
                            {suggestion.type === 'ACHAT' ? t('trading.buy') : t('trading.sell')}
                          </span>
                          <span className={`compact-status ${missed ? 'missed' : 'pending'}`}>
                            {missed ? t('trading.missed') : t('trading.pending')}
                          </span>
                        </div>
                      </div>

                      {/* Infos en ligne compacte */}
                      <div className="compact-details">
                        <div className="compact-detail">
                          <span className="detail-label">{t('trading.amount')}</span>
                          <span className="detail-value">{formatCurrency(suggestion.amount)}</span>
                        </div>
                        <div className="compact-divider"></div>
                        <div className="compact-detail">
                          <span className="detail-label">{t('trading.duration')}</span>
                          <span className="detail-value">{suggestion.duration || '—'}</span>
                        </div>
                        <div className="compact-divider"></div>
                        <div className="compact-detail gain">
                          <span className="detail-label">{t('trading.gain')}</span>
                          <span className="detail-value">+{formatCurrency(suggestion.potentialGain)}</span>
                        </div>
                      </div>

                      {/* Footer avec actions ou état loupé */}
                      {missed ? (
                        <div className="compact-missed">
                          <span className="missed-emoji">😔</span>
                          <span className="missed-text">
                            {t('trading.missedTrade')} à {new Date(suggestion.signalTime || suggestion.expiredAt || suggestion.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span className="missed-gain">+{formatCurrency(suggestion.potentialGain)}</span>
                        </div>
                      ) : (
                        <div className="compact-footer">
                          <div className="compact-countdown">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/>
                              <polyline points="12 6 12 12 16 14"/>
                            </svg>
                            <span>{timeRemaining}</span>
                          </div>
                          <div className="compact-actions">
                            <button className="compact-btn reject" onClick={() => handleReject(suggestion.id)}>
                              {t('trading.reject')}
                            </button>
                            <button className="compact-btn accept" onClick={() => handleAccept(suggestion.id)}>
                              {t('trading.accept')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state-minimal">
                <div className="empty-pulse-container">
                  <div className="empty-pulse p1"></div>
                  <div className="empty-pulse p2"></div>
                  <div className="empty-center-icon">⏳</div>
                </div>
                <p>{t('trading.waitingNext')}</p>
              </div>
            )
          ) : (
            activeTrades.length > 0 ? (
              <div className="trades-list">
                {activeTrades.map((trade) => {
                  const { progress, remaining, isFinished } = getTradeProgress(trade.startedAt, trade.endTime);
                  const isPositive = trade.currentGain >= 0;

                  return (
                    <div key={trade.id} className={`live-trade-card ${isFinished ? 'finished' : 'active'}`}>
                      <div className="live-trade-top">
                        <div className="live-trade-crypto">
                          <div
                            className="crypto-avatar"
                            style={{ background: `linear-gradient(135deg, ${trade.gradient[0]}, ${trade.gradient[1]})` }}
                          >
                            {trade.icon}
                          </div>
                          <span className="live-trade-symbol">{trade.symbol}</span>
                          <span className={`live-trade-type ${trade.type === 'ACHAT' ? 'buy' : 'sell'}`}>
                            {trade.type === 'ACHAT' ? t('trading.buy') : t('trading.sell')}
                          </span>
                        </div>
                        <div className="live-trade-status">
                          {isFinished ? (
                            <span className="live-trade-tag finished">{t('trading.closed')}</span>
                          ) : (
                            <span className="live-trade-tag live">
                              <span className="live-dot"></span>
                              {t('trading.live')}
                            </span>
                          )}
                          <span className="live-trade-time">{isFinished ? t('trading.tradeEnded') : remaining}</span>
                        </div>
                      </div>

                      <div className="live-trade-bar-row">
                        <div className="live-trade-bar-track">
                          <div className="live-trade-bar-fill" style={{ width: `${progress}%` }}></div>
                        </div>
                        <span className="live-trade-percent">{Math.round(progress)}%</span>
                      </div>

                      <div className="live-trade-bottom">
                        <div className="live-trade-meta">
                          <span>{formatCurrency(trade.amount)}</span>
                          <span className="live-trade-meta-sep">·</span>
                          <span>{formatCurrency(trade.entryPrice)} → {formatCurrency(trade.currentPrice)}</span>
                        </div>
                        <span className={`live-trade-gain ${isPositive ? 'positive' : 'negative'}`}>
                          {isPositive ? '+' : ''}{formatCurrency(trade.currentGain)} ({isPositive ? '+' : ''}{trade.currentPercent}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">📊</div>
                <h3>{t('trading.noActiveTrades')}</h3>
                <p>{t('trading.noActivePosDesc')}</p>
              </div>
            )
          )}
        </div>

        {/* How it works */}
        <div className="how-it-works">
          <h3>{t('trading.howItWorks')}</h3>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <span className="step-title">{t('trading.step1Title')}</span>
                <span className="step-desc">{t('trading.step1Desc')}</span>
              </div>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <span className="step-title">{t('trading.step2Title')}</span>
                <span className="step-desc">{t('trading.step2Desc')}</span>
              </div>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <span className="step-title">{t('trading.step3Title')}</span>
                <span className="step-desc">{t('trading.step3Desc')}</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default Trading;
